import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { apiPost } from '../../services/api';

export default function AdminGiftPackageModal({ company, token, onClose, onSuccess }) {
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!company?.id || !token) return;

    setSubmitting(true);
    setFieldErrors({});
    setError('');

    try {
      const res = await apiPost(
        `/api/v1/admin/companies/${company.id}/gift-package`,
        {
          quantity: Number(quantity),
          note: note.trim() || null,
        },
        token
      );

      if (res.status === 422) {
        const json = await res.json();
        setFieldErrors(json.errors ?? {});
        return;
      }

      if (!res.ok) {
        setError('Could not gift listing credits. Please try again.');
        return;
      }

      const json = await res.json();
      onSuccess(json.meta?.message ?? 'Listing credits gifted successfully.');
      onClose();
    } catch {
      setError('Could not gift listing credits. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show
      onHide={onClose}
      centered
      data-testid="rwm-admin-gift-modal"
    >
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Gift Listing Credits — {company.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error ? (
            <Alert variant="danger" className="mb-3" data-testid="rwm-admin-gift-error">
              {error}
            </Alert>
          ) : null}

          <Form.Group className="mb-3" controlId="rwm-admin-gift-quantity-field">
            <Form.Label>Number of listing credits to gift</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={50}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="rwm-admin-gift-quantity"
              isInvalid={Boolean(fieldErrors.quantity)}
            />
            {fieldErrors.quantity ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.quantity[0]}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>

          <Form.Group controlId="rwm-admin-gift-note-field">
            <Form.Label>Internal note (optional, not shown to company)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="rwm-admin-gift-note"
              isInvalid={Boolean(fieldErrors.note)}
            />
            {fieldErrors.note ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.note[0]}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            disabled={submitting}
            data-testid="rwm-admin-gift-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="info"
            type="submit"
            disabled={submitting}
            data-testid="rwm-admin-gift-submit"
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Gifting…
              </>
            ) : (
              'Gift Credits'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
